import { Link, NavLink, useNavigate } from "react-router-dom";
import { Moon, Sun, BookOpen, Sparkles, Menu, X, LogIn, LogOut, User as UserIcon, NotebookText, ShieldCheck, Pencil, GraduationCap, MessageCircle, Bot, LifeBuoy } from "lucide-react";
import { useState } from "react";
import { useTheme } from "@/lib/theme";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { BookmarksMenu } from "@/components/BookmarksMenu";
import { LiveClassesNavCTA } from "@/components/LiveClassesNavCTA";
import { ShareAppButton } from "@/components/PwaActions";

// Primary nav (text links shown md+). "Ask Quran" is demoted to an icon-only
// button in the action area to make room for the Courses link and keep the
// header roomy enough not to push the "Book Live Free Class" CTA off-screen.
const navLinks = [
  { to: "/", label: "Juzz", icon: BookOpen, end: true },
  { to: "/lessons", label: "Courses", icon: GraduationCap },
  { to: "/notes", label: "Notes", icon: NotebookText },
];

const Brand = () => (
  <span className="font-display text-xl sm:text-2xl font-semibold tracking-tight" data-testid="brand-text">
    Easy<span className="text-primary">Quran</span>
    <span className="text-accent">.UK</span>
  </span>
);

export const Navbar = ({ onTogglePen, penActive }) => {
  const { theme, toggle } = useTheme();
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const isAuthed = !!user && user !== null && typeof user === "object";
  const isAdmin = isAuthed && user.role === "admin";

  const onLogout = async () => {
    await logout();
    setOpen(false);
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/75 border-b border-border/60" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group" data-testid="brand-link">
          <span className="w-9 h-9 rounded-full bg-primary/10 border border-accent/40 flex items-center justify-center">
            <BookOpen className="w-4 h-4 text-primary" />
          </span>
          <Brand />
        </Link>

        <nav className="hidden md:flex items-center gap-1" data-testid="primary-nav">
          {navLinks.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`nav-link-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `px-3.5 py-2 text-[15px] font-medium rounded-full transition-colors inline-flex items-center gap-1.5 ${
                  isActive ? "bg-primary/10 text-primary" : "text-foreground hover:text-primary hover:bg-muted"
                }`
              }
            >
              <Icon className="w-4 h-4" /> {label}
            </NavLink>
          ))}
          {isAdmin && (
            <NavLink
              to="/admin"
              data-testid="nav-link-admin"
              className={({ isActive }) =>
                `px-3.5 py-2 text-[15px] font-medium rounded-full transition-colors inline-flex items-center gap-1.5 ${
                  isActive ? "bg-primary/10 text-primary" : "text-foreground hover:text-primary hover:bg-muted"
                }`
              }
            >
              <ShieldCheck className="w-4 h-4" /> Admin
            </NavLink>
          )}
        </nav>

        <div className="flex items-center gap-2">
          {/* PROMINENT, ALWAYS-VISIBLE live-classes CTA. Kept here so that
              even when reading the Quran (where the floating bubble is hidden
              to avoid covering Arabic text) users still see the offer. */}
          <LiveClassesNavCTA />

          {/* Compact icon buttons: Ask Quran bot + Support. These were moved
              out of the primary text nav so the header stays roomy. */}
          <NavLink
            to="/ask"
            data-testid="nav-icon-ask-quran"
            aria-label="Ask the Quran Bot"
            title="Ask the Quran Bot"
            className={({ isActive }) =>
              `relative inline-flex items-center justify-center w-9 h-9 rounded-full transition-colors ${
                isActive
                  ? "bg-primary/10 text-primary"
                  : "text-foreground/75 hover:text-primary hover:bg-muted"
              }`
            }
          >
            <Bot className="w-4 h-4" />
          </NavLink>
          <NavLink
            to="/support"
            data-testid="nav-icon-support"
            aria-label="Contact support"
            title="Contact support"
            className={({ isActive }) =>
              `relative inline-flex items-center justify-center w-9 h-9 rounded-full transition-colors ${
                isActive
                  ? "bg-primary/10 text-primary"
                  : "text-foreground/75 hover:text-primary hover:bg-muted"
              }`
            }
          >
            <LifeBuoy className="w-4 h-4" />
          </NavLink>

          {/* One-tap share — opens the native share sheet on mobile (WhatsApp,
              iMessage, etc.) and copies the link on desktop. */}
          <ShareAppButton variant="icon" />

          <BookmarksMenu />
          <Button
            variant="ghost"
            size="icon"
            onClick={toggle}
            className="rounded-full"
            data-testid="theme-toggle-btn"
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </Button>
          <Button
            variant={penActive ? "default" : "ghost"}
            size="icon"
            onClick={onTogglePen}
            className={`rounded-full ${penActive ? "bg-primary text-primary-foreground" : ""}`}
            data-testid="pen-overlay-toggle-btn"
            aria-label="Toggle annotation pen"
            aria-pressed={!!penActive}
            title="Pen overlay — draw on top of the page (Esc to exit)"
          >
            <Pencil className="w-4 h-4" />
          </Button>
          {isAuthed ? (
            <div className="hidden sm:flex items-center gap-1.5" data-testid="nav-user-area">
              <span className="px-2.5 h-9 rounded-full bg-muted text-foreground/85 text-sm inline-flex items-center gap-1.5" data-testid="nav-user-pill">
                <UserIcon className="w-3.5 h-3.5" /> {user.name || user.email.split("@")[0]}
              </span>
              <Button variant="ghost" size="sm" onClick={onLogout} className="rounded-full" data-testid="nav-logout-btn">
                <LogOut className="w-4 h-4 mr-1" /> Sign out
              </Button>
            </div>
          ) : (
            <Button asChild variant="ghost" size="sm" className="hidden sm:inline-flex rounded-full" data-testid="nav-signin-btn">
              <Link to="/login"><LogIn className="w-4 h-4 mr-1" /> Sign in</Link>
            </Button>
          )}
          <Button
            asChild
            className="hidden sm:inline-flex pill-btn bg-primary hover:bg-primary/90"
            data-testid="cta-start-reading-nav"
          >
            <Link to="/quran/mushaf/juzz/1">
              <Sparkles className="w-4 h-4 mr-1" /> Start Reading
            </Link>
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden rounded-full"
            onClick={() => setOpen(o => !o)}
            data-testid="mobile-menu-btn"
            aria-label="Open menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-border bg-background/95" data-testid="mobile-menu">
          <nav className="flex flex-col px-4 py-2">
            {/* Eye-catching live-classes CTA at the top of the mobile menu */}
            <div className="py-2">
              <LiveClassesNavCTA />
            </div>
            {navLinks.map(({ to, label, icon: Icon, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `px-4 py-3 text-base font-medium rounded-md transition-colors inline-flex items-center gap-2 ${
                    isActive ? "bg-primary/10 text-primary" : "text-foreground hover:bg-muted"
                  }`
                }
              >
                <Icon className="w-4 h-4" /> {label}
              </NavLink>
            ))}
            {/* Demoted-from-text-nav links — exposed here so mobile users can still reach them. */}
            <NavLink
              to="/ask"
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `px-4 py-3 text-base font-medium rounded-md transition-colors inline-flex items-center gap-2 ${
                  isActive ? "bg-primary/10 text-primary" : "text-foreground hover:bg-muted"
                }`
              }
            >
              <Bot className="w-4 h-4" /> Ask Quran
            </NavLink>
            <NavLink
              to="/support"
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `px-4 py-3 text-base font-medium rounded-md transition-colors inline-flex items-center gap-2 ${
                  isActive ? "bg-primary/10 text-primary" : "text-foreground hover:bg-muted"
                }`
              }
            >
              <LifeBuoy className="w-4 h-4" /> Contact / Support
            </NavLink>
            {isAdmin && (
              <NavLink to="/admin" onClick={() => setOpen(false)} className="px-4 py-3 text-base font-medium rounded-md text-foreground hover:bg-muted inline-flex items-center gap-2">
                <ShieldCheck className="w-4 h-4" /> Admin
              </NavLink>
            )}
            <div className="border-t border-border my-2" />
            {isAuthed ? (
              <button onClick={onLogout} className="text-left px-4 py-3 text-sm rounded-md text-foreground/80 hover:bg-muted inline-flex items-center gap-2" data-testid="mobile-logout-btn">
                <LogOut className="w-4 h-4" /> Sign out
              </button>
            ) : (
              <Link to="/login" onClick={() => setOpen(false)} className="px-4 py-3 text-sm rounded-md text-foreground/80 hover:bg-muted inline-flex items-center gap-2" data-testid="mobile-signin-link">
                <LogIn className="w-4 h-4" /> Sign in
              </Link>
            )}
          </nav>
        </div>
      )}
    </header>
  );
};

export const Footer = () => (
  <footer className="mt-16 border-t border-border bg-card/40" data-testid="site-footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid gap-8 md:grid-cols-4">
      <div>
        <Brand />
        <p className="text-sm text-foreground/70 mt-3 leading-relaxed">
          A peaceful, premium platform for reading the Holy Quran — 30 Juzz, 114 Surahs, and live online Quran classes worldwide.
        </p>
      </div>
      <div>
        <h4 className="font-display text-lg">Read</h4>
        <ul className="mt-3 space-y-2 text-sm">
          <li><Link to="/" className="hover:text-primary">Mushaf Library</Link></li>
          <li><Link to="/quran/surahs" className="hover:text-primary">All 114 Surahs</Link></li>
          <li><Link to="/quran/juzz" className="hover:text-primary">All 30 Juzz</Link></li>
          <li><Link to="/quran/surah/36-ya-sin" className="hover:text-primary">Surah Ya-Sin</Link></li>
          <li><Link to="/quran/surah/67-al-mulk" className="hover:text-primary">Surah Al-Mulk</Link></li>
        </ul>
      </div>
      <div>
        <h4 className="font-display text-lg">Learn</h4>
        <ul className="mt-3 space-y-2 text-sm">
          <li><Link to="/lessons" className="hover:text-primary">Online Quran Classes</Link></li>
          <li><Link to="/online-quran-classes/uk" className="hover:text-primary">Quran Classes UK</Link></li>
          <li><Link to="/online-quran-classes/usa" className="hover:text-primary">Quran Classes USA</Link></li>
          <li><Link to="/online-quran-classes/canada" className="hover:text-primary">Quran Classes Canada</Link></li>
          <li><Link to="/blog" className="hover:text-primary">Blog</Link></li>
        </ul>
      </div>
      <div>
        <h4 className="font-display text-lg">Credits</h4>
        <ul className="mt-3 space-y-1.5 text-sm text-foreground/70">
          <li>Text · AlQuran.cloud (Uthmani)</li>
          <li>Recitation · Mishary Rashid Alafasy</li>
          <li>Urdu · Shamshad Ali Khan / Fateh Jalandhry</li>
          <li>English · Ibrahim Walk / Sahih Int&rsquo;l</li>
        </ul>
      </div>
    </div>
    {/* In-short summary — appears site-wide so AI summarisers (Google AI
        Overview, Perplexity, ChatGPT) lift it as the canonical description
        of EasyQuran.UK regardless of which page they crawled. Marked
        `data-speakable` for Google Assistant voice answers. Styled as plain
        prose so it never feels like a callout. */}
    <div className="border-t border-border bg-muted/20">
      <p
        className="speakable max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-[13px] leading-relaxed text-foreground/65 text-center"
        data-speakable="true"
        data-testid="footer-tldr"
      >
        <strong className="text-foreground/80">EasyQuran.UK</strong> is a free online Quran reader and live-class platform.
        Read the complete Holy Quran in 15-line Madani Mushaf style, recite any verse aloud and our AI Voice Sense plays the English or Urdu translation when you pause,
        bookmark ayāt across devices, and book a 1-to-1 live online Quran class with a verified teacher — first trial free.
        Works offline as an installable PWA on iOS &amp; Android.
      </p>
    </div>
    <div className="border-t border-border py-5 text-center text-xs text-foreground/60">
      © {new Date().getFullYear()} EasyQuran.UK — May Allah accept.
    </div>
  </footer>
);
